import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Button,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    Stack,
    Switch,
    FormControlLabel,
    Divider,
    Avatar,
    IconButton,
    Chip,
} from '@mui/material';
import {
    Save as SaveIcon,
    Add as AddIcon,
    CreditCard as BillingIcon,
    Devices as DeviceIcon,
    Settings as GeneralIcon,
    ChevronRight as ChevronRightIcon,
    NotificationsActive as AlertIcon,
    DarkLight as ThemeIcon,
} from '@mui/icons-material';
import settingsService from '../../services/settingsService';
import deviceManagementService from '../../services/deviceManagementService';

const SettingsPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [newDevice, setNewDevice] = useState({ device_id: '', type: '', machine_details: '' });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                await settingsService.getSettings();
            } catch (err) {
                console.error('Failed to fetch settings:', err);
            }
        };
        fetchSettings();
    }, []);

    const handleRegisterDevice = async (e) => {
        e.preventDefault();
        try {
            await deviceManagementService.addDeviceData({
                ...newDevice,
                timestamp: new Date().toISOString()
            });
            alert('Device registered successfully!');
            setNewDevice({ device_id: '', type: '', machine_details: '' });
        } catch (err) {
            console.error('Failed to register device:', err);
            alert('Registration failed.');
        }
    };

    return (
        <Box p={3} className="animate-fade-in">
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '15px' } }}>
                    <Tab icon={<DeviceIcon sx={{ mr: 1 }} />} iconPosition="start" label="Provisioning" />
                    <Tab icon={<BillingIcon sx={{ mr: 1 }} />} iconPosition="start" label="Subscription" />
                    <Tab icon={<GeneralIcon sx={{ mr: 1 }} />} iconPosition="start" label="Preferences" />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Card sx={{ borderRadius: '24px' }}>
                            <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Typography variant="h6" fontWeight={800}>Asset Onboarding</Typography>
                                <Typography variant="caption" color="text.secondary">Register new hardware units to the telematics mesh</Typography>
                            </Box>
                            <CardContent>
                                <form onSubmit={handleRegisterDevice}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Unique Device ID" placeholder="e.g. MEWP-2024-X" value={newDevice.device_id} onChange={(e) => setNewDevice({ ...newDevice, device_id: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField select fullWidth label="System Category" value={newDevice.type} onChange={(e) => setNewDevice({ ...newDevice, type: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                                                <MenuItem value="mewp">MEWP Fleet</MenuItem>
                                                <MenuItem value="battery">Energy Storage</MenuItem>
                                                <MenuItem value="tower">Lighting Tower</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Machine Specifications" placeholder="e.g. JLG Ultra-Series 1200SJP" value={newDevice.machine_details} onChange={(e) => setNewDevice({ ...newDevice, machine_details: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                                            <Button variant="contained" className="gradient-primary" startIcon={<AddIcon />} type="submit" sx={{ px: 4 }}>Authorize Device</Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Card sx={{ height: '100%', bgcolor: 'grey.50' }}>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight={800} mb={2}>REGISTRATION POLICY</Typography>
                                <Stack spacing={2}>
                                    <Typography variant="body2" color="text.secondary">Total registered assets: <strong>48 / Unlimited</strong></Typography>
                                    <Typography variant="body2" color="text.secondary">Last registration: <strong>2 hours ago</strong></Typography>
                                    <Divider />
                                    <Typography variant="caption" color="text.secondary">All hardware must comply with the SecureMQTT protocol version 3.1.1. Registration triggers a security handshake upon first connection.</Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {activeTab === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <Box p={3}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h6" fontWeight={800}>Active Plan</Typography>
                                    <Chip label="PREMIUM" color="info" size="small" sx={{ fontWeight: 900, borderRadius: '6px' }} />
                                </Stack>
                                <Box p={3} bgcolor="primary.main" color="white" borderRadius="20px">
                                    <Typography variant="h3" fontWeight={800}>$1,200<Typography component="span" variant="h6">/year</Typography></Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }} mt={1}>Enterprise Tier Agreement • Auto-renews Jan 2026</Typography>
                                </Box>
                                <Button fullWidth variant="outlined" sx={{ mt: 3, borderRadius: '12px', fontWeight: 800 }}>Manage Billing Cycles</Button>
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <Box p={3}>
                                <Typography variant="h6" fontWeight={800} mb={3}>Payment Architecture</Typography>
                                <Box display="flex" alignItems="center" gap={2} p={2} border="1px solid" borderColor="divider" borderRadius="16px">
                                    <Avatar sx={{ bgcolor: 'grey.100' }}><BillingIcon sx={{ color: 'grey.800' }} /></Avatar>
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle2" fontWeight={800}>Visa Corporate •••• 4242</Typography>
                                        <Typography variant="caption" color="text.secondary">Primary Funding Source • Expiry 12/28</Typography>
                                    </Box>
                                    <Button size="small" sx={{ fontWeight: 800 }}>Edit</Button>
                                </Box>
                                <Button fullWidth variant="text" color="inherit" sx={{ mt: 2, fontWeight: 700 }}>+ Add Redundant Method</Button>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {activeTab === 2 && (
                <Card sx={{ maxWidth: 800 }}>
                    <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={800}>Operational Preferences</Typography>
                    </Box>
                    <CardContent>
                        <Stack spacing={4}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>Telemetry Broadcaster</Typography>
                                    <Typography variant="body2" color="text.secondary">Real-time system health alerts via SMS and Email</Typography>
                                </Box>
                                <Switch defaultChecked />
                            </Box>
                            <Divider />
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>Maintenance Predictor</Typography>
                                    <Typography variant="body2" color="text.secondary">AI-driven scheduling for proactive asset repair</Typography>
                                </Box>
                                <Switch defaultChecked />
                            </Box>
                            <Divider />
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>Geometric Mode</Typography>
                                    <Typography variant="body2" color="text.secondary">Toggle high-contrast interface for outdoor usage</Typography>
                                </Box>
                                <Switch />
                            </Box>
                            <Box display="flex" justifyContent="flex-end" pt={2}>
                                <Button variant="contained" className="gradient-primary" startIcon={<SaveIcon />} sx={{ px: 4 }}>Commit Changes</Button>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default SettingsPage;
